/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  ADD_FAMILY_INFORMATION_FACADE_TOKEN,
  IAddFamilyInformationFacade,
} from '../../../../../../data-access/src/lib/domain/facades-models/add-family-information-facade-interface';
import { PropertiesListItemsVM } from '../../../../../../../shared/ui/src/lib/models/sysque-properties-list-vm';
import { SysqueSwitchButtonVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-switch-button-vm';
import { SysqueDropdownConnectorsVM } from '../../../../../../../shared/ui/src/lib/models/sysque-dropdown-connectors-vm';
import {
  SysqueSizeRangeItemsVM,
  SysqueSizeRangeVM,
} from '../../../../../../../shared/ui/src/lib/models/sysque-size-range-vm';
import {
  AddFamilyInformationData,
  AddFamilyInformationReturnCodes,
  FamilyInformationItem,
} from '../../../models/family-information-model';
import { GlobalConstants } from '../../../constants/global-constants';
import { SysqueNumberBoxVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-number-box-vm';
import { FieldFabConduitSizeRangeVM, IFamilyInformationItem } from '../add-family-information-types';
import { filter, Subscription } from 'rxjs';
import { LineGraphicsDialogConfig } from '../line-graphics';
import { CreateServiceTypeDialogConfig } from '../create-service-type';
import { DeleteServiceTypeDialogConfig } from '../delete-service-type';
import { AddFamilyProjectDataInfo } from '../../../../../../utils/src/lib/common-helper/add-family-project-data-info';
import { AlertStatus, IAlert } from '../../../../../../../shared/ui/src/lib/components/alert/alert-types';

@Component({
  selector: 'field-fab-family-information',
  templateUrl: './field-fab-family-information.component.html',
  styleUrls: ['./field-fab-family-information.component.scss'],
  standalone: false
})
export class FieldFabFamilyInformation implements OnInit, OnDestroy {
  //#region Public members

  public alertStatus = AlertStatus.Warning;
  public warningMessage = '';
  public title = '';
  public image = '';
  public propertiesListItems: PropertiesListItemsVM[] = [];
  public isLengthVisible = false;
  public lengthSpinButtons = true;
  public lengthItems: SysqueNumberBoxVM[] = [];
  public isServiceTypeVisible: boolean = true;
  public serviceTypeName = '';
  public serviceTypeData: string[] = [];
  public serviceTypePlaceholder = 'Service Type';
  public angleSpinButtons = true;
  public isAngleVisible = false;
  public angleItems: SysqueNumberBoxVM[] = [];
  public isPanelCircuitVisible = false;
  public panelCircuitButtonInfo: SysqueSwitchButtonVM;
  public areConnectorsVisible = false;
  public connectorsTitle: string;
  public connectorsItems: SysqueDropdownConnectorsVM[] = [];
  public fileName = '';
  public isSupportDisciplineVisible = false;
  public supportDisciplineLabel: string;
  public supportDiscipline: string;
  public instanceDescriptionLabel = '';
  public instanceDescription = '';
  public typeDescriptionLabel = '';
  public typeDescription = '';
  public projectDataButtonInfo: SysqueSwitchButtonVM;
  public isHangerVisible = true;
  public hangerButtonInfo: SysqueSwitchButtonVM;
  public isFieldFabConduitVisible = true;
  public fieldFabConduitButtonInfo: SysqueSwitchButtonVM;
  public dataGrid: SysqueSizeRangeVM;
  public diagram = '';
  public selectedPathSubscription: Subscription;
  public openPdfSubscription: Subscription;
  public item: IFamilyInformationItem;
  public lineGraphicsDialogConfig: LineGraphicsDialogConfig;
  public isLineGraphicsDialogOpen = false;
  public menuItems = [
    { label: GlobalConstants.AddServiceMenuItem, disabled: false, imageSrc: 'assets/img/Add-Black.png' },
    { label: GlobalConstants.RemoveServiceMenuItem, disabled: false, imageSrc: 'assets/img/Delete.png' },
    { label: GlobalConstants.LineGraphicsMenuItem, disabled: false, imageSrc: 'assets/img/Line-Graphics.png' },
  ];
  public createServiceTypeDialogConfig: CreateServiceTypeDialogConfig;
  public isCreateServiceTypeDialogOpen = false;

  public deleteServiceTypeDialogConfig: DeleteServiceTypeDialogConfig;
  public isDeleteServiceTypeDialogOpen = false;
  public isProjectDataBtnDisabled = false;

  public isAlertVisible = false;

  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  //#endregion

  //#region Private members

  private selectedPath: string = '';
  private selectedSizeRangeData: SysqueSizeRangeItemsVM[] = [];

  //#endregion

  constructor(
    @Inject(ADD_FAMILY_INFORMATION_FACADE_TOKEN) private addFamilyInformationFacade: IAddFamilyInformationFacade
  ) {}

  public ngOnInit(): void {
    window.addEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));
    this.addFamilyInformationFacade.registerControllers();
    const response = this.addFamilyInformationFacade.getFamilyInformationResponse();
    if (response.Data.IsFromAdd) {
      this.item = this.addFamilyInformationFacade.setItemToAdd(response.Data);
    } else {
      this.item = this.addFamilyInformationFacade.getFamilyInformationItem();
    }
    this.addFamilyInformationFacade.updateLocalStorage(GlobalConstants.PROJECT_PARAMS, []);
    this.setFamilyInformationData(response.Data, this.item);
    this.handleMessages(response);
    this.initData();
  }

  public ngOnDestroy(): void {
    if (this.selectedPathSubscription) {
      this.selectedPathSubscription.unsubscribe();
    }
    if (this.openPdfSubscription) {
      this.openPdfSubscription.unsubscribe();
    }
    window.removeEventListener('storage', this.handleStorageEvent.bind(this));
  }

  /**
   * Handles the click event of the project data button.
   */
  public onProjectDataButtonClicked(): void {
    this.addFamilyInformationFacade.onClickedToOpenProjectData();
  }

  /**
   * Handles the click event of the project data switch button.
   * @param button - The SysqueSwitchButtonVM representing the clicked button.
   */
  public onProjectDataSwitchClicked(button: SysqueSwitchButtonVM): void {
    this.isProjectDataBtnDisabled = !button.enabled;
  }

  //#region data initialization
  private initData(): void {
    this.titleInit();
    this.imageInit();
    this.propertiesInit();
    this.lengthInit();
    this.serviceTypeInit();
    this.angleInit();
    this.connectorsInit();
    this.pdfLocationInit();
    this.instanceDescriptionInit();
    this.typeDescriptionInit();
    this.fieldFabConduitInit();
    this.projectDataInit();
    this.panelCircuitInit();
    this.sizeRangeInit();
  }

  private titleInit(): void {
    if (this.item.isFromDuplicate || (this.item.itemType === 'ElectricalTypeItems' && this.item.isFromAdd)) {
      this.title = GlobalConstants.NEW_CONDUIT_TITLE;
    } else {
      this.title = this.item.name;
    }
  }

  private imageInit(): void {
    this.image = 'data:image/png;base64,' + this.item.image;
  }

  private propertiesInit(): void {
    this.propertiesListItems = [
      {
        label:
          this.item.isFromDuplicate || (this.item.itemType === 'ElectricalTypeItems' && this.item.isFromAdd)
            ? GlobalConstants.CONDUIT_NAME_LABEL
            : GlobalConstants.ITEM_TYPE_LABEL,
        value: this.item.isFromDuplicate
          ? ''
          : this.item.itemType === 'ElectricalTypeItems'
            ? this.item.name
            : this.item.fittingType,
        isEditable: this.item.isFromDuplicate || (this.item.itemType === 'ElectricalTypeItems' && this.item.isFromAdd),
      },
      {
        label: GlobalConstants.MANUFACTURER_LABEL,
        value: this.item.manufacturer,
        isEditable: false,
      },
    ];

    if (this.isElectricalTypeItems) {
      this.propertiesListItems.push({
        label: GlobalConstants.MATERIAL_LABEL,
        value: this.item.material,
        isEditable: !this.item.isFromEdit && !this.item.isFromAdd && !this.item.isFromDuplicate,
      });
    }

    this.propertiesListItems.push({
      label: GlobalConstants.ID_LABEL,
      value: this.item.id,
      isEditable: false,
    });

    if (this.item.isEquipment) {
      this.propertiesListItems.push({
        label: GlobalConstants.ADDED_WIRE_LENGTH_LABEL,
        value: this.item.addedWireLength,
        isEditable: !this.item.isFromEdit && !this.item.isFromAdd,
      });
    }
  }

  private lengthInit(): void {
    this.isLengthVisible = this.isElectricalTypeItems;
    this.lengthItems = [
      {
        label: GlobalConstants.LENGTH_LABEL,
        value: this.isMetric
          ? this.addFamilyInformationFacade.convertFeetToMm(Number(this.item.length))
          : Number(this.item.length),
        isMetric: this.isMetric,
        height: '30',
        width: '120',
        minValue: 0,
        isInFeet: !this.isMetric,
      },
    ];
  }

  private serviceTypeInit(): void {
    this.serviceTypeName = GlobalConstants.SERVICE_TYPE_LABEL;
    this.isServiceTypeVisible = this.isElectricalTypeItems;
    this.serviceTypeData = this.item.serviceTypes
      ?.sort((a, b) => a.ServiceName.localeCompare(b.ServiceName))
      .map((x) => x.ServiceName);
    this.item.serviceType = this.serviceTypePlaceholder =
      this.item.serviceType === '' ? this.serviceTypeData[0] : this.item.serviceType;
  }

  private angleInit(): void {
    this.angleSpinButtons = this.isElectricalTypeItems;
    this.isAngleVisible =
      (this.isAddDefaultItem &&
        !(
          this.item.fittingType.includes(GlobalConstants.UNION) ||
          this.item.fittingType.includes(GlobalConstants.TRANSITION) ||
          this.item.fittingType.includes(GlobalConstants.NORMAL) ||
          (this.item.fittingType.includes(GlobalConstants.MULTI_PORT) && this.item.angle == '')
        )) ||
      this.isElectricalTypeItems;
    this.angleItems = [
      {
        label: this.isElectricalTypeItems ? GlobalConstants.MAX_BEND_ANGLE_LABEL : GlobalConstants.ANGLE_LABEL,
        value: this.isAddDefaultItem === true ? Number(this.item.angle) : Number(this.item.totalBendRun),
        isAngle: true,
        height: '30',
        width: '124',
        disabled: !this.isElectricalTypeItems,
        minValue: 0,
      },
    ];
  }

  public connectorsInit(): void {
    if (this.item.connectorCount === 0) {
      return;
    }
    const itemConnectors = this.item.connectors;
    let selectedConnectors = [];
    if (itemConnectors) {
      selectedConnectors = itemConnectors?.split(' x ').map((x) => x.trim());
    }
    this.connectorsTitle = GlobalConstants.CONNECTORS_LABEL;
    const connectors = this.addFamilyInformationFacade.getConduitConnectors();
    const connectorsCount = this.item.connectorCount;
    this.areConnectorsVisible = connectorsCount > 0;
    for (let i = 0; i < connectorsCount; i++) {
      this.connectorsItems.push({
        label: `C${i + 1}`,
        data: connectors.map((x) => x.connector),
        selectedValue: Array.isArray(selectedConnectors)
          ? connectors?.find((x) => x.connector === selectedConnectors[i])?.connector
          : connectors?.find((x) => x.connector === selectedConnectors)?.connector,
      });
    }
  }

  private pdfLocationInit(): void {
    this.fileName = !this.item.pdfLocation ? '' : this.item.pdfLocation.split('\\').pop()?.split('/').pop() || '';
  }

  private instanceDescriptionInit(): void {
    this.instanceDescriptionLabel = GlobalConstants.INSTANCE_DESCRIPTION_LABEL;
    this.instanceDescription = this.item.descriptionInstance;
  }

  private typeDescriptionInit(): void {
    this.typeDescriptionLabel = GlobalConstants.TYPE_DESCRIPTION_LABEL;
    this.typeDescription = this.item.descriptionType;
  }

  private fieldFabConduitInit(): void {
    this.fieldFabConduitButtonInfo = {
      title: GlobalConstants.FIELD_FAB_CONDUIT_LABEL,
      enabled: this.item.isUpgradeFieldFabBend,
      labelForEnabled: GlobalConstants.ENABLED,
      labelForDisabled: GlobalConstants.DISABLED,
      hasOptionButton: false,
    };
  }

  private projectDataInit(): void {
    this.projectDataButtonInfo = {
      title: GlobalConstants.PROJECT_DATA_LABEL,
      enabled: Array.isArray(this.item.parameters) && this.item.parameters.length > 0,
      labelForEnabled: GlobalConstants.ENABLED,
      labelForDisabled: GlobalConstants.DISABLED,
      hasOptionButton: true,
    };
  }

  private panelCircuitInit(): void {
    this.isPanelCircuitVisible = this.isElectricalTypeItems || (this.isAddDefaultItem && this.item.panelCircuitChecked);
    this.panelCircuitButtonInfo = {
      title: GlobalConstants.PANEL_CIRCUIT_LABEL,
      enabled: this.item.panelCircuitChecked,
      labelForEnabled: GlobalConstants.ENABLED,
      labelForDisabled: GlobalConstants.DISABLED,
      hasOptionButton: false,
    };
  }

  private sizeRangeInit(): void {
    this.dataGrid = new SysqueSizeRangeVM();
    this.dataGrid.title = GlobalConstants.SIZE_RANGE_LABEL;
    const itemSizeRange: FieldFabConduitSizeRangeVM = new FieldFabConduitSizeRangeVM(
      this.item.sizeRange,
      this.item.bendRadius,
      this.item.leadLength,
      this.item.takeUp
    );
    this.dataGrid.sizeRangeItems =
      this.addFamilyInformationFacade.convertFieldFabStringToSysqueSizeRangeVM(itemSizeRange);
    this.selectedSizeRangeData = this.dataGrid.sizeRangeItems?.filter((x) => x.isChecked === 1);
  }

  private handleMessages(response: FamilyInformationItem): void {
    if (
      response.ReturnCode == AddFamilyInformationReturnCodes.Success ||
      response.ReturnCode == AddFamilyInformationReturnCodes.NoMessage
    ) {
      this.hideAlert();
    } else if (response.ReturnCode == AddFamilyInformationReturnCodes.InvalidFamilies) {
      this.showAlert(GlobalConstants.PLEASE_UPDATE_FAMILIES_AND_TRY_AGAIN, AlertStatus.Warning);
    }
  }

  //#endregion

  public onPropertiesValueChanged($event: { value: string; label: string }): void {
    switch ($event.label) {
      case GlobalConstants.CONDUIT_NAME_LABEL:
        this.item.name = $event.value;
        break;
      case GlobalConstants.ITEM_TYPE_LABEL:
        this.item.partType = $event.value;
        break;
      case GlobalConstants.MANUFACTURER_LABEL:
        this.item.manufacturer = $event.value.toString();
        break;
      case GlobalConstants.ID_LABEL:
        this.item.id = $event.value.toString();
        break;
    }
  }

  public onLengthValueChanged($event: { value: string; label: string }): void {
    this.item.length = this.isMetric
      ? this.addFamilyInformationFacade.convertMmToFeet(Number($event.value))?.toString()
      : $event.value;
  }

  public onServiceTypeValueChanges($event: string): void {
    this.item.serviceType = $event.toString();
  }

  public onAngleValueChanged($event: { value: string; label: string }): void {
    this.isAddDefaultItem === true ? (this.item.angle = $event.value) : (this.item.totalBendRun = $event.value);
  }

  public onPanelCircuitClicked(button: SysqueSwitchButtonVM): void {
    this.item.panelCircuitChecked = button.enabled;
  }

  public onConnectorValueChanged($event: { value: string; label: string }): void {
    const index = Number($event.label.replace('C', '')) - 1;
    let selectedConnectors;
    if(this.item.connectors === null) {
      selectedConnectors = [];
    } else {
      selectedConnectors = this.item.connectors.split(' x ').map((x) => x.trim());
    }
    selectedConnectors[index] = $event.value;
    this.item.connectors = selectedConnectors.join(' x ');
  }

  public onAttachFileClicked(): void {
    this.selectedPathSubscription = this.addFamilyInformationFacade.getPdfLocationPath().subscribe((data) => {
      if (!data) {
        return;
      }
      this.selectedPath = data;
      this.fileName = data.split('\\').pop()?.split('/').pop() || '';
      this.item.pdfLocation = data;
    });
  }

  public onOpenPdf(): void {
    this.openPdfSubscription = this.addFamilyInformationFacade
      .openPdfFile(this.item.pdfLocation)
      .subscribe((response) => {
        if (response == AddFamilyInformationReturnCodes.PdfDoesNotExist) {
          this.showAlert(GlobalConstants.PDF_FILE_DOES_NOT_EXIST, AlertStatus.Warning);
        }
      });
  }

  public onFieldFabConduitClicked(button: SysqueSwitchButtonVM): void {
    this.item.isUpgradeFieldFabBend = button.enabled;
  }

  public onInstanceDescriptionValueChanged($event: string): void {
    this.item.descriptionInstance = $event;
  }

  public onTypeDescriptionValueChanged($event: string): void {
    this.item.descriptionType = $event;
  }

  public onSizeRangeSelectedItems($event: SysqueSizeRangeItemsVM[]): void {
    this.selectedSizeRangeData = $event;
    this.dataGrid.sizeRangeItems.forEach((element) => {
      element.isChecked = $event.some((x) => x.id === element.id) ? 1 : 0;
    });
    this.handleDataGridCellChanges();
  }

  public onCellValueChanged(): void {
    this.handleDataGridCellChanges();
  }

  public handleDataGridCellChanges(): void {
    const fieldFabSizeRange = this.addFamilyInformationFacade.convertSysqueSizeRangeItemsVMToFieldFab(
      this.dataGrid.sizeRangeItems
    );
    this.item.sizeRange = fieldFabSizeRange?.sizeRange;
    this.item.bendRadius = fieldFabSizeRange?.bendRadius;
    this.item.leadLength = fieldFabSizeRange?.leadLength;
    this.item.takeUp = fieldFabSizeRange?.takeUp;
  }

  public doneClick(): void {
    if (!this.validateInput()) {
      return;
    }
    new AddFamilyProjectDataInfo(this.addFamilyInformationFacade).getModifiedProjectParameters(
      this.item,
      this.isProjectDataBtnDisabled
    );
    this.addFamilyInformationFacade.saveFamilyInformationInXml(this.item);
  }

  public handleStorageEvent(event: StorageEvent): void {
    if (event.key === 'familyInformationSaveResponse') {
      if (event.newValue === '') {
        return;
      }
      const response = JSON.parse(event.newValue);
      if (response == AddFamilyInformationReturnCodes.Success) {
        this.addFamilyInformationFacade.updateItem(this.item);
        this.addFamilyInformationFacade.sendClosePopupAction();
      } else if (response == AddFamilyInformationReturnCodes.InvalidFamilies) {
        this.showAlert(GlobalConstants.PLEASE_UPDATE_FAMILIES_AND_TRY_AGAIN, AlertStatus.Warning);
      } else if (response == AddFamilyInformationReturnCodes.IdError) {
        this.showAlert(GlobalConstants.PLEASE_ENTER_ID_FIELD, AlertStatus.Warning);
      } else if (response == AddFamilyInformationReturnCodes.NameAlreadyExistsInSystem) {
        this.showAlert(GlobalConstants.CONDUIT_ALREADY_EXISTS_IN_ANOTHER_SYSTEM, AlertStatus.Warning);
      } else if (response == AddFamilyInformationReturnCodes.NameAlreadyExistsInRevit) {
        this.showAlert(GlobalConstants.CONDUIT_NAME_ALREADY_EXISTS_IN_REVIT, AlertStatus.Warning);
      } else if (response == AddFamilyInformationReturnCodes.FamilyAlreadyAddedInSystem) {
        this.showAlert(GlobalConstants.THIS_FAMILY_IS_ALREADY_ADDED_IN_SYSTEM, AlertStatus.Warning);
      }
    }
  }

  public validateInput(): boolean {
    if (this.item.name === '') {
      this.showAlert(GlobalConstants.PLEASE_ENTER_CONDUIT_NAME, AlertStatus.Warning);
      return false;
    }
    if (this.isLengthVisible && Number(this.item.length) <= 0) {
      this.showAlert(GlobalConstants.LENGTH_SHOULD_BE_GREATER_THAN_ZERO, AlertStatus.Warning);
      return false;
    }
    if (this.isElectricalTypeItems && this.selectedSizeRangeData?.length === 0) {
      this.showAlert(GlobalConstants.PLEASE_SELECT_AT_LEAST_ONE_SIZE, AlertStatus.Warning);
      return false;
    }
    return true;
  }

  /**
   * Handles the context menu item click
   * @param item - the item that was clicked
   */
  public handleContextMenuItem(item: string): void {
    switch (item) {
      case GlobalConstants.AddServiceMenuItem:
        this.openCreateServiceTypeDialog();
        break;
      case GlobalConstants.RemoveServiceMenuItem:
        this.openDeleteServiceTypeDialog();
        break;
      case GlobalConstants.LineGraphicsMenuItem:
        this.openLineGraphicsDialog();
        break;
    }
  }

  //#region alert
  public showAlert(message: string, alertStatus: AlertStatus): void {
    this.warningMessage = message;
    this.alertStatus = alertStatus;
    this.isAlertVisible = true;
  }

  public hideAlert(): void {
    this.warningMessage = '';
    this.isAlertVisible = false;
  }

  //#endregion

  private get isElectricalTypeItems(): boolean {
    return this.item.itemType === GlobalConstants.ELECTRICAL_TYPE_ITEMS;
  }

  private get isAddDefaultItem(): boolean {
    return this.item.itemType === GlobalConstants.ADD_DEFAULT_ITEM;
  }

  private get isMetric(): boolean {
    return this.item.unit === GlobalConstants.METRIC;
  }

  private setFamilyInformationData(data: AddFamilyInformationData, item: IFamilyInformationItem): void {
    item.serviceTypes = data.ServiceTypes;
    item.electricalType = data.ElectricalType;
    item.isClearence = data.IsClearance;
    item.partType = data.PartType;
    if (this.isAddDefaultItem) {
      item.fittingType = data.FittingType;
      item.isEquipment = data.IsEquipment;
    }
    item.angle = data.Angle ?? item.angle;
    item.isWall = data.IsWall;
    item.bendType = data.BendType;
    item.connectorCount = data.ConnectorCount;
    item.length = data.Length ?? item.length;
    item.csvName = data.CsvName;
    item.centerRadius = data.CenterRadius;
    item.diameter = data.Diameter;
    item.height = data.Height;
    item.width = data.Width;
    item.material = data.Material ?? item.material;
    item.panelCircuitChecked = data.PanelCircuitChecked;
    item.familyCategoryName = data.FamilyCategoryName;
    this.addFamilyInformationFacade.updateLocalStorage(GlobalConstants.FAMILY_INFORMATION_ITEM, item);
  }

  // #region Create Service Type

  //Set the dialog config for create service type
  public openCreateServiceTypeDialog(): void {
    this.createServiceTypeDialogConfig = {
      serviceTypeName: '',
      serviceTypes: this.item.serviceTypes.map((x) => x.ServiceName),
      apply: () => this.applyCreateServiceTypeChanges(),
      close: () => this.closeCreateServiceTypeDialog(),
    };
    // Open dialog
    this.isCreateServiceTypeDialogOpen = true;
  }

  //Apply the changes made in the dialog
  public applyCreateServiceTypeChanges(): void {
    //apply the changes
    const serviceType = this.addFamilyInformationFacade.applyCreateServiceTypeChanges(
      this.item,
      this.createServiceTypeDialogConfig
    );
    this.serviceTypePlaceholder = this.serviceTypeName = this.createServiceTypeDialogConfig.serviceTypeName;
    this.serviceTypeData = this.item.serviceTypes.map((x) => x.ServiceName);
    //close the dialog
    this.closeCreateServiceTypeDialog();
    //save in the backend the new service type
    this.addFamilyInformationFacade.saveServiceType(serviceType);
  }

  //Close the create service type dialog
  private closeCreateServiceTypeDialog(): void {
    this.isCreateServiceTypeDialogOpen = false;
  }

  // #region Remove Service Type

  //Set the dialog config for delete service type
  public openDeleteServiceTypeDialog(): void {
    this.deleteServiceTypeDialogConfig = {
      apply: () => this.applyDeleteServiceTypeChanges(),
      close: () => this.closeDeleteServiceTypeDialog(),
    };
    // Open dialog
    this.isDeleteServiceTypeDialogOpen = true;
  }

  //Apply the changes made in the dialog
  public applyDeleteServiceTypeChanges(): void {
    const selectedServiceType = this.item.serviceTypes.find((x) => x.ServiceName === this.item.serviceType);
    if (!this.addFamilyInformationFacade.applyDeleteServiceTypeChanges(this.item)) {
      return;
    }
    this.serviceTypePlaceholder = this.serviceTypeName = this.item.serviceType = '<None>';
    this.serviceTypeData = this.item.serviceTypes.map((x) => x.ServiceName);
    //close the dialog
    this.closeDeleteServiceTypeDialog();
    //delete in the backend the service type
    this.addFamilyInformationFacade.deleteServiceType(selectedServiceType);
  }

  //Close the delete service type dialog
  private closeDeleteServiceTypeDialog(): void {
    this.isDeleteServiceTypeDialogOpen = false;
  }

  // #region Line Graphics

  /**
   * Opens the line graphics dialog
   */
  public openLineGraphicsDialog(): void {
    // Get patterns from facade
        let patterns;
        this.addFamilyInformationFacade.getPatterns().pipe(filter(data => data !== undefined))
        .subscribe((data) => {
          patterns = data;
          // Set dialog config here
          this.lineGraphicsDialogConfig = {
            color: this.item.serviceTypeColor ? this.item.serviceTypeColor : '#000000',
            patterns: patterns,
            currentPattern: this.item.linePattern ? this.item.linePattern : patterns[0],
            lineWeight: this.item.lineWeight ? Number(this.item.lineWeight) : 1,
            apply: () => this.applyChanges(),
            close: () => this.closeLineGraphicsDialog(),
          };
          
          // Open dialog
          this.isLineGraphicsDialogOpen = true;
        });
  }

  /**
   * Updates the line graphics info with the changes made in the dialog
   */
  public applyChanges(): void {
    // Apply line graphics changes
    this.item = this.addFamilyInformationFacade.applyChangesForLineGraphics(this.item, this.lineGraphicsDialogConfig);

    this.closeLineGraphicsDialog();
  }

  /**
   * Closes the line graphics dialog
   */
  public closeLineGraphicsDialog(): void {
    this.isLineGraphicsDialogOpen = false;
  }
}
